/** Minimal plain-object vector math so the placement logic stays engine-free and unit-testable. */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const v3 = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
export const add = (a: Vec3, b: Vec3): Vec3 => v3(a.x + b.x, a.y + b.y, a.z + b.z);
export const sub = (a: Vec3, b: Vec3): Vec3 => v3(a.x - b.x, a.y - b.y, a.z - b.z);
export const scale = (a: Vec3, s: number): Vec3 => v3(a.x * s, a.y * s, a.z * s);
export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const length = (a: Vec3): number => Math.sqrt(dot(a, a));
export const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => add(a, scale(sub(b, a), t));
export const UP: Vec3 = v3(0, 1, 0);

export function normalize(a: Vec3): Vec3 {
  const len = length(a);
  return len > 1e-6 ? scale(a, 1 / len) : v3(0, 0, 0);
}

/** Flatten to the ground plane and normalise; +Z forward when degenerate (three.js convention: forward is -Z, so callers pass the true forward). */
export function flatten(a: Vec3, fallback: Vec3): Vec3 {
  const flat = v3(a.x, 0, a.z);
  return dot(flat, flat) < 0.001 ? fallback : normalize(flat);
}

/** Yaw in degrees of a ground-plane direction, matching Unity's `Atan2(x, z)`. */
export function yawDegrees(direction: Vec3): number {
  return (Math.atan2(direction.x, direction.z) * 180) / Math.PI;
}

/** Ground-plane direction for a yaw in degrees. */
export function directionFromYaw(yawDeg: number): Vec3 {
  const rad = (yawDeg * Math.PI) / 180;
  return v3(Math.sin(rad), 0, Math.cos(rad));
}

/** Signed shortest difference `to - from` in degrees, in [-180, 180]. */
export function deltaAngle(from: number, to: number): number {
  let delta = (to - from) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

/** Exponential-smoothing step factor for a time constant (seconds). */
export function smoothFactor(deltaSeconds: number, timeConstantSeconds: number): number {
  return 1 - Math.exp(-deltaSeconds / Math.max(0.0001, timeConstantSeconds));
}

/** Move a scalar toward a target by at most `maxDelta`. */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  if (Math.abs(target - current) <= maxDelta) return target;
  return current + Math.sign(target - current) * maxDelta;
}
